import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelText as _vModelText, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "add-employee-form" }
const _hoisted_2 = { class: "work-day-hours" }
const _hoisted_3 = { class: "work-day-hours-label" }
const _hoisted_4 = { class: "work-day-hours-inputs" }
const _hoisted_5 = ["onUpdate:modelValue"]
const _hoisted_6 = {
  key: 0,
  class: "loading apollo"
}

import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";
import { useMutation, useQuery } from "@vue/apollo-composable";
import { logErrorMessages } from "@vue/apollo-util";

import Modal from "@/components/Modal.vue";
import FormItem from "@/components/form/FormItem.vue";
import BasicButton from "@/components/BasicButton.vue";
import ImageUploader from "@/components/ImageUploader.vue";

import { CREATE_USER } from "@/graphql/user/mutations";
import { GET_DETAILED_EMPLOYEES } from "@/graphql/user/queries";
import { GET_DEFAULT_ROLES } from "@/graphql/defaultRole/queries";

import enUK from "@/locales/en-UK.json";
import { DefaultRole } from "@/helpers/user/typesDefinition";


export default /*@__PURE__*/_defineComponent({
  __name: 'AddEmployees',
  emits: ["close"],
  setup(__props, { emit: __emit }) {

const emit = __emit;

const { t } = useI18n({
  useScope: "global",
  inheritLocale: true,
  messages: {
    "en-UK": enUK,
  },
});

const toast = useToast();

// Fetch roles for the dropdown
const { result: rolesResult } = useQuery(GET_DEFAULT_ROLES);

const newEmployeeFirstName = ref("");
const newEmployeeLastName = ref("");
const newEmployeeEmail = ref("");
const newEmployeeRoleId = ref("");
const newEmployeeAvatarUrl = ref("");

// Default work day hours: Mon=8, Tue=8, Wed=8, Thu=8, Fri=8, Sat=0, Sun=0
const workDayHours = ref<number[]>([0, 8, 8, 8, 8, 8, 0]);

const selectedRegion = ref<string>("BE");

const DAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// Compute role options for the dropdown
const roleOptions = computed(() => {
  if (!rolesResult.value?.defaultRoles?.data) return [];
  return rolesResult.value.defaultRoles.data.map((role: DefaultRole) => ({
    name: role.name,
    id: role.id,
  }));
});

const {
  mutate: createEmployee,
  onError: onCreateEmployeeError,
  onDone: onCreateEmployeeDone,
  loading: creatingEmployee,
} = useMutation(CREATE_USER, {
  refetchQueries: [{ query: GET_DETAILED_EMPLOYEES }],
});

onCreateEmployeeError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.createEmployee"));
});

onCreateEmployeeDone(() => {
  toast.success(t("success.createEmployee"));
  emit("close");
});

const isLoading = computed(() => creatingEmployee.value);

async function handleCreateEmployee(): Promise<void> {
  if (
    !newEmployeeFirstName.value ||
    !newEmployeeLastName.value ||
    !newEmployeeEmail.value ||
    !newEmployeeRoleId.value
  ) {
    toast.error(t("errors.requiredFields"));
    return;
  }

  await createEmployee({
    firstName: newEmployeeFirstName.value,
    lastName: newEmployeeLastName.value,
    email: newEmployeeEmail.value,
    defaultRoleId: newEmployeeRoleId.value || null,
    avatarUrl: newEmployeeAvatarUrl.value || null,
    workDayHours: workDayHours.value,
    region: selectedRegion.value || undefined,
  });
}

function handleAvatarChange(fileId: string): void {
  newEmployeeAvatarUrl.value = fileId;
}

function closeModal(): void {
  emit("close");
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, { onClose: closeModal }, {
    header: _withCtx(() => [
      _createTextVNode(_toDisplayString(_unref(t)("titles.employee.add")), 1)
    ]),
    body: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(FormItem, {
          input: newEmployeeFirstName.value,
          "onUpdate:input": _cache[0] || (_cache[0] = ($event: any) => ((newEmployeeFirstName).value = $event)),
          label: _unref(t)('labels.firstName'),
          name: "firstName",
          "input-type": "basic"
        }, null, 8, ["input", "label"]),
        _createVNode(FormItem, {
          input: newEmployeeLastName.value,
          "onUpdate:input": _cache[1] || (_cache[1] = ($event: any) => ((newEmployeeLastName).value = $event)),
          label: _unref(t)('labels.lastName'),
          name: "lastName",
          "input-type": "basic"
        }, null, 8, ["input", "label"]),
        _createVNode(FormItem, {
          input: newEmployeeEmail.value,
          "onUpdate:input": _cache[2] || (_cache[2] = ($event: any) => ((newEmployeeEmail).value = $event)),
          label: _unref(t)('labels.email'),
          name: "email",
          "input-type": "basic"
        }, null, 8, ["input", "label"]),
        _createVNode(FormItem, {
          input: newEmployeeRoleId.value,
          "onUpdate:input": _cache[3] || (_cache[3] = ($event: any) => ((newEmployeeRoleId).value = $event)),
          label: _unref(t)('labels.role'),
          name: "role",
          "input-type": "select",
          options: roleOptions.value
        }, null, 8, ["input", "label", "options"]),
        _createVNode(ImageUploader, {
          "current-url": newEmployeeAvatarUrl.value,
          onChanged: handleAvatarChange
        }, null, 8, ["current-url"]),
        _createVNode(FormItem, {
          input: selectedRegion.value,
          "onUpdate:input": _cache[4] || (_cache[4] = ($event: any) => ((selectedRegion).value = $event)),
          label: _unref(t)('labels.region'),
          name: "region",
          "input-type": "select",
          options: [
            { name: 'BE', id: 'BE' },
            { name: 'FR', id: 'FR' },
          ]
        }, null, 8, ["input", "label"]),
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(t)("labels.workDayHours")), 1),
          _createElementVNode("div", _hoisted_4, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(DAY_LABELS, (label, index) => {
              return _createElementVNode("div", {
                key: index,
                class: "work-day-hour-input"
              }, [
                _createElementVNode("label", null, _toDisplayString(label), 1),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": ($event: any) => ((workDayHours.value[index]) = $event),
                  type: "number",
                  min: "0",
                  max: "24",
                  step: "0.5"
                }, null, 8, _hoisted_5), [
                  [_vModelText, workDayHours.value[index]]
                ])
              ])
            }), 64))
          ])
        ])
      ]),
      (isLoading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(_unref(t)("loading")), 1))
        : _createCommentVNode("", true)
    ]),
    footer: _withCtx(() => [
      _createVNode(BasicButton, {
        class: "add-button",
        type: "submit",
        disabled: isLoading.value,
        onClick: handleCreateEmployee
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)("buttons.addEmployee")), 1)
        ]),
        _: 1
      }, 8, ["disabled"]),
      _createVNode(BasicButton, {
        class: "cancel-button",
        onClick: closeModal
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)("buttons.cancel")), 1)
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})